import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LocalizationLike, VoiceStatus } from './useAssistantChat';
import { ComposerBox, IconButton, RecorderStatus, RecordingDot, Spinner, TextArea, Timer } from './styled';
import { ChatIcon, CheckIcon, CloseIcon, MicrophoneIcon, SendIcon } from './icons';

const LINE_HEIGHT = 20;
const VERTICAL_PADDING = 8;

function formatElapsed(ms: number) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function RecordingTimer({ startedAt }: { startedAt: number | null }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(timer);
    }, []);

    return <Timer data-testid="assistant-chat-recording-timer">{formatElapsed(startedAt ? now - startedAt : 0)}</Timer>;
}

interface ComposerProps {
    localization: LocalizationLike;
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    canSend: boolean;
    isReady: boolean;
    isInitializing: boolean;
    maxRows: number;
    voice: {
        status: VoiceStatus;
        startedAt: number | null;
        isAvailable: boolean;
        canRecord: boolean;
        start: () => void;
        finish: () => void;
        cancel: () => void;
    };
    onExpand?: () => void;
    autoFocus?: boolean;
}

export function Composer({
    localization,
    value,
    onChange,
    onSubmit,
    canSend,
    isReady,
    isInitializing,
    maxRows,
    voice,
    onExpand,
    autoFocus
}: ComposerProps) {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const previousVoiceStatus = useRef<VoiceStatus>(voice.status);
    const t = (key: string) => localization.getText(`assistant_chat.${key}`);

    // Auto-grow from one line up to `maxRows`, then scroll inside the field.
    useLayoutEffect(() => {
        const el = textAreaRef.current;
        if (!el) return;
        const maxHeight = maxRows * LINE_HEIGHT + VERTICAL_PADDING;
        el.style.height = 'auto';
        const nextHeight = Math.min(el.scrollHeight, maxHeight);
        el.style.height = `${Math.max(nextHeight, LINE_HEIGHT + VERTICAL_PADDING)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [value, maxRows, voice.status]);

    // After a transcription lands in the field, put the caret at the end so the user can edit or send.
    useEffect(() => {
        const wasTranscribing = previousVoiceStatus.current === 'transcribing';
        previousVoiceStatus.current = voice.status;
        if (wasTranscribing && voice.status === 'idle' && textAreaRef.current) {
            const el = textAreaRef.current;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
    }, [voice.status]);

    useEffect(() => {
        if (autoFocus && voice.status === 'idle') {
            textAreaRef.current?.focus();
        }
    }, [autoFocus]);

    const isVoiceActive = voice.status !== 'idle';

    // Init failures are reported (with Retry) in the notice above the composer, not in the placeholder.
    const placeholder = isInitializing ? t('initializing') : t('input_placeholder');

    return (
        <ComposerBox $focused={isFocused} $recording={voice.status === 'recording'} data-testid="assistant-chat-composer">
            {isVoiceActive ? (
                <>
                    <IconButton type="button" onClick={voice.cancel} title={t('voice_cancel')} aria-label={t('voice_cancel')}>
                        <CloseIcon size={16} />
                    </IconButton>
                    <RecorderStatus role="status" aria-live="polite">
                        {voice.status === 'recording' ? (
                            <>
                                <RecordingDot />
                                <span>{t('recording')}</span>
                                <RecordingTimer startedAt={voice.startedAt} />
                            </>
                        ) : (
                            <>
                                <Spinner />
                                <span>{t('transcribing')}</span>
                            </>
                        )}
                    </RecorderStatus>
                    <IconButton
                        type="button"
                        $tone="danger"
                        onClick={voice.finish}
                        disabled={voice.status !== 'recording'}
                        title={t('voice_confirm')}
                        aria-label={t('voice_confirm')}>
                        <CheckIcon size={16} />
                    </IconButton>
                </>
            ) : (
                <>
                    {onExpand && (
                        <IconButton type="button" onClick={onExpand} title={t('expand_button')} aria-label={t('expand_button')}>
                            <ChatIcon size={18} />
                        </IconButton>
                    )}
                    <TextArea
                        ref={textAreaRef}
                        rows={1}
                        value={value}
                        placeholder={placeholder}
                        disabled={!isReady}
                        aria-label={t('input_placeholder')}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onChange={event => onChange(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                                event.preventDefault();
                                if (canSend) onSubmit();
                            }
                        }}
                    />
                    {voice.isAvailable && (
                        <IconButton
                            type="button"
                            onClick={voice.start}
                            disabled={!voice.canRecord}
                            title={t('voice_input_button')}
                            aria-label={t('voice_input_button')}>
                            <MicrophoneIcon size={18} />
                        </IconButton>
                    )}
                    <IconButton
                        type="button"
                        $tone="primary"
                        onClick={onSubmit}
                        disabled={!canSend}
                        title={t('send_button')}
                        aria-label={t('send_button')}>
                        <SendIcon size={16} />
                    </IconButton>
                </>
            )}
        </ComposerBox>
    );
}
